import React from "react";
import { Link } from "react-router-dom";

// Crawlable, genuinely useful supporting content for the (otherwise thin) auth
// pages — improves text-to-HTML ratio and word count for SEO without harming UX.
const CONTENT = {
  register: {
    heading: "Turn any PDF into a study deck",
    lead: "LoomPDF is a study tool for students, exam candidates and lifelong learners. Create a free account and upload your first PDF — a textbook chapter, lecture notes, a research paper or interview prep — and LoomPDF rebuilds it into a horizontal deck of clear, bite-sized study sections you can walk through instead of endlessly scrolling.",
    lead2: "Most study apps make you copy notes by hand or re-read the same wall of text over and over. LoomPDF does the heavy lifting for you: it reads the whole document, splits it into focused sections, and turns each one into something you can actively study. That means less time organising and more time actually learning — whether you are cramming the night before an exam or building a long-term revision habit.",
    points: [
      "AI section breakdown: long documents become 6–14 focused cards, one idea each, with page references back to the source.",
      "Auto-generated quizzes & flashcards drawn straight from your document, so every question tests your actual material.",
      "Export any deck or quiz to PDF or PowerPoint to revise offline or share with a study group.",
      "Works great for textbooks, class notes, competitive-exam prep, research papers and professional whitepapers.",
      "Start free — no card required. Upgrade to Pro or Lifetime any time for unlimited decks and larger documents.",
    ],
    foot: "Already have an account?",
    footLink: "/login",
    footLinkText: "Sign in to LoomPDF",
  },
  login: {
    heading: "Welcome back to LoomPDF",
    lead: "Sign in to reach your study decks, resume a document you were working through, and generate fresh quizzes from your PDFs. LoomPDF keeps every deck you have created — from textbook chapters to exam prep — organised and ready to review in a single sitting.",
    lead2: "Your account remembers where you left off, so you can jump straight back into revision without hunting for files or re-uploading anything. Everything you have turned into a study deck stays in one place, and you can always create new flashcards and quizzes from documents you have already added. It is the fastest way to get from a dense PDF to focused, active study.",
    points: [
      "Pick up exactly where you left off in any horizontal study deck.",
      "Generate new flashcards and multiple-choice quizzes from the PDFs you have already uploaded.",
      "Export decks and quizzes to PDF or PowerPoint for offline revision.",
      "Track your plan, activity emails and account settings from your dashboard.",
      "Access your decks from any device — desktop, tablet or phone.",
    ],
    foot: "New to LoomPDF?",
    footLink: "/register",
    footLinkText: "Create your free account",
  },
  forgot: {
    heading: "Trouble signing in to LoomPDF?",
    lead: "If you forgot your password, enter the email address you used to sign up and we will send a secure reset link that expires in one hour. For your security, the link can only be used once, and we never reveal whether an email address has an account.",
    lead2: "Password resets are one of the most common reasons people get locked out of an account, so we have made the process as quick and safe as possible. Once you request a link, check your inbox within a few minutes. If you originally signed up using Google, you will not have a password to reset at all — simply use the Google sign-in option and you will be straight back into your study decks.",
    points: [
      "Reset links are single-use and expire after 60 minutes for safety.",
      "Signed up with Google? Skip the reset and use “Continue with Google” on the sign-in page instead.",
      "Didn’t get the email? Check your spam folder, then request a new link in a few minutes.",
      "Make sure you enter the same email address you originally registered with.",
      "Still stuck? Contact our team and we’ll help you get back into your decks.",
    ],
    foot: "Need more help?",
    footLink: "/contact",
    footLinkText: "Contact LoomPDF support",
  },
};

export default function AuthSeoContent({ variant = "register" }) {
  const c = CONTENT[variant] || CONTENT.register;
  return (
    <section className="mt-14 text-stone-600" data-testid={`auth-seo-${variant}`} aria-label="About LoomPDF">
      <h2 className="font-display text-2xl text-stone-800">{c.heading}</h2>
      <p className="mt-3 text-sm leading-relaxed">{c.lead}</p>
      <p className="mt-3 text-sm leading-relaxed">{c.lead2}</p>
      <ul className="mt-4 space-y-2 text-sm">
        {c.points.map((p, i) => (
          <li key={i} className="flex gap-2 leading-relaxed">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-indigo-400 shrink-0" />
            <span>{p}</span>
          </li>
        ))}
      </ul>
      <p className="mt-5 text-sm">
        {c.foot}{" "}
        <Link to={c.footLink} className="text-indigo-600 hover:underline">{c.footLinkText}</Link>.
      </p>
    </section>
  );
}
